import type { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";

let stripePromise: Promise<Stripe> | null = null;

/**
 * Stripe.js, loaded once and shared.
 *
 * From the package's `/pure` entry: the main entry injects the Stripe.js
 * script tag as a side effect of merely being imported, and the adapter
 * registry imports this module on every page whether or not Stripe is the
 * active processor. A page with no card form on it has no business loading a
 * payment SDK, so the script goes in only when `loadStripe` runs — on window
 * load via `preload`, or when the card fields mount. A failed load is
 * forgotten rather than memoized, so the next attempt tries again instead of
 * failing for the life of the page.
 */
export const getStripe = (): Promise<Stripe> => {
  if (!stripePromise) {
    const key = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
    stripePromise = (
      key
        ? loadStripe(key)
        : Promise.reject(new Error("VITE_STRIPE_PUBLIC_KEY is not set."))
    )
      .then((stripe) => {
        if (!stripe) {
          throw new Error("Stripe.js loaded without initializing.");
        }
        return stripe;
      })
      .catch((error) => {
        stripePromise = null;
        throw error;
      });
  }
  return stripePromise;
};

/** Warm the SDK. Resolves once tokenizing is safe. */
export const loadStripeJs = (): Promise<void> =>
  getStripe().then(() => undefined);
